import { Injectable } from '@angular/core';

import { EventModel } from '@frontend/sports/betting-offer/feature/model';
import { BetBuilderConfig, EpcotConfig } from '@frontend/sports/common/client-config-data-access';
import { LocalStoreService, SessionStoreService } from '@frontend/vanilla/core';
import { Observable, Subject, map, of } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class BetBuilderOnboardingMessageService {
    private STORAGE_KEY: string;
    private readonly SESSION_STORAGE_KEY = 'betBuilderOnboardingAngstromShown';
    private readonly START_VALUE = 0;
    private _sportsCastVisible$ = new Subject<boolean>();
    private readonly isEpcotEnabled: boolean;
    private eventId: string;
    private isCurrentTabSportsCast: boolean;
    private eventSame: boolean;
    private shownForSameEvent: boolean;

    constructor(
        private localStoreService: LocalStoreService,
        private configuration: BetBuilderConfig,
        private epcotConfig: EpcotConfig,
        private sessionStoreService: SessionStoreService,
    ) {
        this.isEpcotEnabled = this.epcotConfig.isEnabled;
    }

    shouldShow(sportId: number): boolean {
        return this.isEpcotEnabled && !this.isCounterFinished() && this.isWhitelistedSport(sportId);
    }

    incrementCounter(): void {
        this.tryMarkAsShown();

        if (this.eventSame && !this.isCurrentTabSportsCast) {
            return;
        }

        this.set(this.get() + 1);
    }

    isCounterFinished(): boolean {
        return this.get() > this.maxValue;
    }

    sportsCastVisible(value: boolean): void {
        this._sportsCastVisible$.next(value);
    }

    visible$(event: EventModel, isSportscastTab: boolean): Observable<boolean> {
        this.STORAGE_KEY = isSportscastTab ? 'betBuilderOnboardingMessageCounter' : 'angstromOnboardingMessageCounter';
        this.isCurrentTabSportsCast = isSportscastTab;
        const visible = this.shouldShow(event.sport.id);
        this.eventSame = this.eventId === event.id;
        this.eventId = event.id;

        if (isSportscastTab) {
            return this._sportsCastVisible$.asObservable().pipe(map((x) => x && visible));
        }

        if (!this.eventSame) {
            this.shownForSameEvent = false;
        }

        const onboardingShown = this.sessionStoreService.get<boolean>(this.SESSION_STORAGE_KEY);
        const sessionStoreValid = !onboardingShown || (this.eventSame && this.shownForSameEvent);

        return of(visible && !!event.betBuilderInfo?.betBuilderId && sessionStoreValid);
    }

    close(): void {
        if (!this.isCurrentTabSportsCast) {
            this.set(this.maxValue + 1);
        }
    }

    tryMarkAsShown(): void {
        if (!this.isCurrentTabSportsCast && !this.isCounterFinished()) {
            this.sessionStoreService.set(this.SESSION_STORAGE_KEY, true);
            this.shownForSameEvent = true;
        }
    }

    private isWhitelistedSport(sportId: number): boolean {
        if (this.configuration.onboardingMessageEnabledSports) {
            return this.configuration.onboardingMessageEnabledSports.includes(sportId);
        }

        return false;
    }

    private get(): number {
        return this.localStoreService.get<number>(this.STORAGE_KEY) ?? this.START_VALUE;
    }

    private set(value: number): void {
        return this.localStoreService.set(this.STORAGE_KEY, value);
    }

    private get maxValue(): number {
        return this.configuration.onboardingMessageMaxAppearances ?? 3;
    }
}
